import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import { firebaseApp, db } from "@healthdesk/shared-services";
import { useForm, Controller } from "react-hook-form";
import {
  Box,
  Typography,
  TextField,
  Alert,
  Paper,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import { contentCardSx } from "../../styles/standaloneLayoutTokens";
import { LoadingButton } from "@mui/lab";
import {
  ChatRounded,
  MedicalServicesRounded,
  LocalHospitalRounded,
  MedicalInformationRounded,
  CheckCircleOutlineRounded,
  HelpOutlineRounded,
  CampaignRounded,
} from "@mui/icons-material";
import EmergencySearch from "../EmergencySearch";
import AddressSearch from "../AddressSearch";
import { getAddress } from "@healthdesk/shared-utils";
import { getStateCode } from "@healthdesk/shared-utils";
import * as geohash from "ngeohash";
import {
  consumeCarouselSlideSignupAttribution,
  emitMapCarouselEvent,
} from "../../services/mapCarousel";
import { FACILITY_TYPE } from "../../constants/facilityTypes";
import {
  deriveFacilitySubtypes,
  isClinicEnrollmentType,
} from "./accountSetupFacilitySubtypes";

const STEPS = {
  ACCOUNT_TYPE: 0,
  PROFILE: 1,
  FACILITY_TYPE: 2,
  FACILITY_INFO: 3,
  SUCCESS: 4,
};

/**
 * AccountSetupFlow - Shared account setup steps for new users
 *
 * Used by both PhoneAuthFlow (after phone verification) and Onboarding page.
 * Handles: account type selection, profile info, facility type, facility details.
 *
 * @param {string} phoneNumber - Phone number to display (optional, shown as disabled field)
 * @param {function} onComplete - Called with user object when setup is complete
 * @param {function} onStepChange - Called when step changes (for parent to track)
 * @param {object} backHandlerRef - Ref to expose back handler to parent
 * @param {boolean} showBackOnFirstStep - Whether to show back button on account type step
 * @param {function} onBackFromFirstStep - Called when back is pressed on first step
 * @param {string} preselectedRole - Pre-selected role (patient|provider|facility|tbd), skips account type step
 * @param {function} onSignupStart - Called before Firestore write to prevent redirect race condition
 * @param {function} onSuccessStep - Called with true/false when entering/leaving success step
 * @param {string} [returnTo] - Post-setup redirect path (e.g. /symptomchecker); used for button label
 * @param {object|null} [prefillProfile] - Optional pre-filled profile values
 */
const AccountSetupFlow = ({
  phoneNumber,
  onComplete,
  onStepChange,
  backHandlerRef,
  showBackOnFirstStep = false,
  onBackFromFirstStep,
  preselectedRole = null,
  onSignupStart,
  onSuccessStep,
  returnTo = null,
  prefillProfile = null,
}) => {
  const navigate = useNavigate();

  // Determine initial step - preselectedRole only skips ACCOUNT_TYPE, not other steps
  const getInitialStep = () => {
    if (preselectedRole) {
      return STEPS.PROFILE; // Skip only account type selection
    }
    return STEPS.ACCOUNT_TYPE;
  };

  const [step, setStepInternal] = useState(getInitialStep);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedRole, setSelectedRole] = useState(preselectedRole || "");
  const [profileData, setProfileData] = useState(null);
  const [clinicCardSelected, setClinicCardSelected] = useState(false);

  // Wrapper to notify parent of step changes
  const setStep = (newStep) => {
    setStepInternal(newStep);
    onStepChange?.(newStep);
  };

  // Sync preselectedRole on mount - ensures step is correct even with React quirks
  useEffect(() => {
    if (preselectedRole) {
      // Ensure role is set and we're at PROFILE step (not ACCOUNT_TYPE)
      if (selectedRole !== preselectedRole) {
        setSelectedRole(preselectedRole);
      }
      if (step === STEPS.ACCOUNT_TYPE) {
        setStepInternal(STEPS.PROFILE);
        onStepChange?.(STEPS.PROFILE);
        return;
      }
    }
    onStepChange?.(step);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { errors },
    watch,
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      firstName: prefillProfile?.firstName || "",
      lastName: prefillProfile?.lastName || "",
      email: prefillProfile?.email || "",
      facilityType: "",
      facilityName: "",
      address: null,
      emergency_location: null,
    },
  });

  const watchFacilityType = watch("facilityType");

  useEffect(() => {
    if (!prefillProfile) return;
    const currentValues = getValues();
    reset({
      ...currentValues,
      firstName: prefillProfile?.firstName || currentValues.firstName || "",
      lastName: prefillProfile?.lastName || currentValues.lastName || "",
      email: prefillProfile?.email || currentValues.email || "",
    });
  }, [getValues, prefillProfile, reset]);

  // Handle role selection
  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    setStep(STEPS.PROFILE);
  };

  // Handle profile submission
  const handleProfileSubmit = async (data) => {
    // Safeguard: ensure we're on the right step and have required data
    if (step !== STEPS.PROFILE) {
      console.warn("handleProfileSubmit called from wrong step:", step);
      return;
    }
    if (!data.firstName?.trim() || !data.lastName?.trim()) {
      setError("Please fill in your first and last name.");
      return;
    }

    const effectiveRoleSelection = selectedRole;
    const roleToSave =
      effectiveRoleSelection === "provider"
        ? "professional"
        : effectiveRoleSelection === "chartmind-provider"
          ? "professional-chartmind"
          : effectiveRoleSelection === "tbd"
            ? "tbd"
            : effectiveRoleSelection === "promotional-sponsor"
              ? "advertiser"
              : effectiveRoleSelection;

    // For facilities, store profile data and move to facility type selection
    if (roleToSave === "facility") {
      setProfileData(data);
      setStep(STEPS.FACILITY_TYPE);
      return;
    }

    // For non-facilities, create user document now
    setLoading(true);
    setError("");

    // Prevent redirect race condition
    onSignupStart?.();

    try {
      const user = firebaseApp.auth().currentUser;
      if (!user) {
        throw new Error("No authenticated user found.");
      }

      await db
        .collection("users")
        .doc(user.uid)
        .set({
          uid: user.uid,
          phone: phoneNumber || user.phoneNumber || null,
          phoneVerified: !!(phoneNumber || user.phoneNumber),
          email: data.email || user.email || null,
          name: data.firstName,
          lastName: data.lastName,
          role: roleToSave,
          registrationDate: firebase.firestore.FieldValue.serverTimestamp(),
          onboarding: false,
        });

      const carouselSlideId = consumeCarouselSlideSignupAttribution();
      if (carouselSlideId) {
        void emitMapCarouselEvent({ slideId: carouselSlideId, kind: "signup" });
      }

      // Show success step
      onSuccessStep?.(true);
      setStep(STEPS.SUCCESS);
    } catch (err) {
      console.error("Error creating profile:", err);
      setError(err.message || "Failed to create profile.");
    } finally {
      setLoading(false);
    }
  };

  // Handle facility info submission
  const handleFacilityInfoSubmit = async (data) => {
    setLoading(true);
    setError("");

    // Prevent redirect race condition
    onSignupStart?.();

    try {
      const user = firebaseApp.auth().currentUser;
      if (!user) {
        throw new Error("No authenticated user found.");
      }

      const facilityType = data.facilityType;
      const locationId =
        facilityType === "hospital"
          ? String(data.emergency_location?.id)
          : db.collection("locations").doc().id;

      // Create batch for Firestore operations
      const batch = db.batch();

      // Set up user document
      const userRef = db.collection("users").doc(user.uid);
      batch.set(userRef, {
        uid: user.uid,
        phone: phoneNumber || user.phoneNumber || null,
        phoneVerified: !!(phoneNumber || user.phoneNumber),
        email: profileData?.email || user.email || null,
        name: profileData?.firstName,
        lastName: profileData?.lastName,
        role: "facility",
        location: locationId,
        registrationDate: firebase.firestore.FieldValue.serverTimestamp(),
        onboarding: false,
      });

      // Handle location data
      const locationsRef = db.collection("locations").doc(locationId);

      if (facilityType === "hospital") {
        batch.set(
          locationsRef,
          {
            users: firebase.firestore.FieldValue.arrayUnion(user.uid),
          },
          { merge: true }
        );
      } else if (isClinicEnrollmentType(facilityType)) {
        const addressData = data.address;
        const lookup = (name) =>
          addressData.context.find((a) => a?.id.startsWith(name))?.text;

        const formattedAddress = getAddress(addressData);
        const latitude = addressData.center[1];
        const longitude = addressData.center[0];
        const geopoint = new firebase.firestore.GeoPoint(latitude, longitude);
        const hash = geohash.encode(latitude, longitude);
        const facilitySubtypes = deriveFacilitySubtypes(facilityType);

        batch.set(locationsRef, {
          id: locationId,
          title: data.facilityName,
          lat: latitude,
          lng: longitude,
          address: formattedAddress,
          city: lookup("place"),
          state: getStateCode(addressData),
          zip: lookup("postcode"),
          type: FACILITY_TYPE.CLINIC,
          facilitySubtypes,
          users: firebase.firestore.FieldValue.arrayUnion(user.uid),
          status: "pending",
          coordinates: geopoint,
          g: {
            geohash: hash,
            geopoint,
          },
        });
      }

      // Commit all Firestore changes
      await batch.commit();

      const carouselSlideId = consumeCarouselSlideSignupAttribution();
      if (carouselSlideId) {
        void emitMapCarouselEvent({ slideId: carouselSlideId, kind: "signup" });
      }

      // Show success step
      onSuccessStep?.(true);
      setStep(STEPS.SUCCESS);
    } catch (err) {
      console.error("Error creating facility:", err);
      setError(err.message || "Failed to create facility.");
    } finally {
      setLoading(false);
    }
  };

  // Universal back handler
  const handleBack = () => {
    if (step === STEPS.ACCOUNT_TYPE) {
      onBackFromFirstStep?.();
    } else if (step === STEPS.PROFILE) {
      setStep(STEPS.ACCOUNT_TYPE);
      setSelectedRole("");
      setError("");
    } else if (step === STEPS.FACILITY_TYPE) {
      setStep(STEPS.PROFILE);
      setProfileData(null);
      setClinicCardSelected(false);
      setError("");
    } else if (step === STEPS.FACILITY_INFO) {
      setStep(STEPS.FACILITY_TYPE);
      setError("");
    }
  };

  // Expose handleBack to parent via ref
  useEffect(() => {
    if (backHandlerRef) {
      backHandlerRef.current = handleBack;
    }
  });

  // Account type card component
  const AccountTypeCard = ({ icon, title, description, selected, onClick }) => (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        ...contentCardSx,
        p: 2.5,
        cursor: "pointer",
        display: "flex",
        alignItems: "flex-start",
        gap: 2,
        border: "2px solid",
        borderColor: selected ? "primary.main" : "divider",
        backgroundColor: selected ? "rgba(25, 118, 210, 0.04)" : "white",
        transition: "all 0.15s ease-in-out",
        "&:hover": {
          backgroundColor: "rgba(25, 118, 210, 0.04)",
        },
      }}
    >
      <Box sx={{ color: "primary.main", mt: 0.5 }}>{icon}</Box>
      <Box>
        <Typography variant="subtitle1" fontWeight={600}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </Box>
    </Paper>
  );

  // Facility type card component
  const FacilityTypeCard = ({
    icon,
    title,
    description,
    selected,
    onClick,
  }) => (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        ...contentCardSx,
        p: 3,
        cursor: "pointer",
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        border: "2px solid",
        borderColor: selected ? "primary.main" : "divider",
        backgroundColor: selected ? "rgba(25, 118, 210, 0.04)" : "white",
        transition: "all 0.15s ease-in-out",
        "&:hover": {
          backgroundColor: "rgba(25, 118, 210, 0.04)",
        },
      }}
    >
      <Box sx={{ color: "primary.main", mb: 1.5 }}>{icon}</Box>
      <Typography variant="subtitle1" fontWeight={600}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Paper>
  );

  // Format phone for display
  const displayPhone =
    phoneNumber || firebaseApp.auth().currentUser?.phoneNumber;
  const formattedPhone = displayPhone || "";

  return (
    <Box sx={{ width: "100%" }}>
      {/* Account Type Selection */}
      {step === STEPS.ACCOUNT_TYPE && (
        <>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
            Choose your account type
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Select how you'll use HealthDesk.
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <AccountTypeCard
              icon={<ChatRounded fontSize="large" />}
              title="Patient"
              description="Get symptom insights, find care, and manage your health journey."
              selected={selectedRole === "patient"}
              onClick={() => handleRoleSelect("patient")}
            />

            <AccountTypeCard
              icon={<MedicalServicesRounded fontSize="large" />}
              title="Healthcare Provider"
              description="Access clinical decision support and patient management tools."
              selected={selectedRole === "provider"}
              onClick={() => handleRoleSelect("provider")}
            />

            <AccountTypeCard
              icon={<LocalHospitalRounded fontSize="large" />}
              title="Healthcare Organization"
              description="Manage your facility, staff, and streamline patient flow."
              selected={selectedRole === "facility"}
              onClick={() => handleRoleSelect("facility")}
            />

            <AccountTypeCard
              icon={<CampaignRounded fontSize="large" />}
              title="Promotional Sponsor"
              description="Run zip-targeted carousel ads on CareMap after admin approval."
              selected={selectedRole === "promotional-sponsor"}
              onClick={() => handleRoleSelect("promotional-sponsor")}
            />

            <AccountTypeCard
              icon={<HelpOutlineRounded fontSize="large" />}
              title="To be determined (TBD)"
              description="Create an account first; you'll get your role from a join link or QR code from your team."
              selected={selectedRole === "tbd"}
              onClick={() => handleRoleSelect("tbd")}
            />
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}
        </>
      )}

      {step === STEPS.PROFILE && (
        <>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
            Create your HealthDesk account
          </Typography>

          <form onSubmit={handleSubmit(handleProfileSubmit)}>
            {formattedPhone && (
              <TextField
                label="Phone Number"
                value={formattedPhone}
                fullWidth
                variant="outlined"
                disabled
                sx={{ mb: 2 }}
              />
            )}

            <Controller
              name="firstName"
              control={control}
              rules={{ required: "First name is required." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="First name"
                  fullWidth
                  variant="outlined"
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message}
                  sx={{ mb: 2 }}
                />
              )}
            />

            <Controller
              name="lastName"
              control={control}
              rules={{ required: "Last name is required." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Last name"
                  fullWidth
                  variant="outlined"
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message}
                  sx={{ mb: 2 }}
                />
              )}
            />

            <Controller
              name="email"
              control={control}
              rules={{
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                  message: "Invalid email address.",
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Email"
                  type="email"
                  fullWidth
                  variant="outlined"
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  sx={{ mb: 3 }}
                />
              )}
            />

            <LoadingButton
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              loading={loading}
              sx={{ py: 1.5 }}
            >
              {selectedRole === "facility" ? "Next" : "Create account"}
            </LoadingButton>

            {error && (
              <Alert
                severity="error"
                sx={{ mt: 2 }}
                onClose={() => setError("")}
              >
                {error}
              </Alert>
            )}
          </form>
        </>
      )}

      {/* Facility Type Selection */}
      {step === STEPS.FACILITY_TYPE && (
        <>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
            What type of facility?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Select the type of healthcare facility you&apos;re registering.
          </Typography>

          {/* Note: facilitySubtypes is the field the future `caremap-manager` role
              will manage. For now, set at enrollment by facility/f2 role.
              See ROLES_AND_PERMISSIONS.md — caremap-manager surface TBD. */}
          <Controller
            name="facilityType"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <FacilityTypeCard
                  icon={<LocalHospitalRounded sx={{ fontSize: 40 }} />}
                  title="Emergency Department"
                  description="24-hour emergency care"
                  selected={value === "hospital"}
                  onClick={() => {
                    setClinicCardSelected(false);
                    onChange("hospital");
                    setStep(STEPS.FACILITY_INFO);
                  }}
                />
                <FacilityTypeCard
                  icon={<MedicalInformationRounded sx={{ fontSize: 40 }} />}
                  title="Clinic or Urgent Care"
                  description="Includes primary care offices, specialist practices, and urgent care / immediate care centers that accept walk-in patients."
                  selected={clinicCardSelected}
                  onClick={() => {
                    setClinicCardSelected(true);
                    onChange("");
                  }}
                />
                {clinicCardSelected ? (
                  <Paper
                    elevation={0}
                    sx={{
                      ...contentCardSx,
                      p: 2.5,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography variant="subtitle2" gutterBottom>
                      Does your facility accept unscheduled walk-in patients?
                    </Typography>
                    <RadioGroup
                      value={value === "urgent-care" || value === "clinic-only" ? value : ""}
                      onChange={(e) => onChange(e.target.value)}
                    >
                      <FormControlLabel
                        value="urgent-care"
                        control={<Radio size="small" />}
                        label="Yes — we are an urgent / immediate care"
                      />
                      <FormControlLabel
                        value="clinic-only"
                        control={<Radio size="small" />}
                        label="No — appointments only"
                      />
                    </RadioGroup>
                    <Button
                      variant="contained"
                      fullWidth
                      disabled={value !== "urgent-care" && value !== "clinic-only"}
                      onClick={() => setStep(STEPS.FACILITY_INFO)}
                      sx={{ mt: 2 }}
                    >
                      Continue
                    </Button>
                  </Paper>
                ) : null}
              </Box>
            )}
          />

          {error && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}
        </>
      )}

      {/* Facility Info */}
      {step === STEPS.FACILITY_INFO && (
        <>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
            Facility details
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {watchFacilityType === "hospital"
              ? "Search for your emergency department."
              : "Enter your facility name and address."}
          </Typography>

          <form onSubmit={handleSubmit(handleFacilityInfoSubmit)}>
            {watchFacilityType === "hospital" ? (
              <EmergencySearch control={control} errors={errors} />
            ) : (
              <>
                <Controller
                  name="facilityName"
                  control={control}
                  rules={{ required: "Facility name is required." }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Facility Name"
                      fullWidth
                      variant="outlined"
                      error={!!errors.facilityName}
                      helperText={errors.facilityName?.message}
                      sx={{ mb: 2 }}
                    />
                  )}
                />
                <AddressSearch control={control} errors={errors} />
              </>
            )}

            <LoadingButton
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              loading={loading}
              sx={{ py: 1.5, mt: 3 }}
            >
              Complete registration
            </LoadingButton>

            {error && (
              <Alert
                severity="error"
                sx={{ mt: 2 }}
                onClose={() => setError("")}
              >
                {error}
              </Alert>
            )}
          </form>
        </>
      )}

      {/* Success */}
      {step === STEPS.SUCCESS && (
        <Box sx={{ textAlign: "center" }}>
          <CheckCircleOutlineRounded
            sx={{ fontSize: 64, color: "success.main", mb: 2 }}
          />
          <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
            You're all set!
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Your account has been created successfully.
            {selectedRole === "facility" && (
              <>
                {" "}
                Your facility is pending review and will be visible on CareMap
                once approved.
              </>
            )}
          </Typography>

          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={() => {
              if (onComplete) {
                onComplete(firebaseApp.auth().currentUser);
              } else {
                navigate("/dashboard");
              }
            }}
            sx={{ py: 1.5 }}
          >
            {returnTo === "/symptomchecker"
              ? "Continue To Symptom Checker"
              : returnTo === "/getonthemap"
                ? "Continue to Get on the map"
                : returnTo?.startsWith("/provider/cmr-share")
                  ? "Continue to patient share"
                  : "Continue to Dashboard"}
          </Button>
        </Box>
      )}
    </Box>
  );
};

// Export step constants for parent components
export { STEPS as ACCOUNT_SETUP_STEPS };
export default AccountSetupFlow;
